// A regra dos follow-ups: cadência, e-mail automático (janela, template com
// campos), e "sem resposta → perdido". Do lado, a prévia com um negócio de
// verdade e o "enviar teste para mim". As duas chaves que mudam o mundo lá fora
// (e-mail automático e perda automática) pedem confirmação ao ligar.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  App as AntApp, Alert, Button, Card, Checkbox, Divider, Input, InputNumber, Modal, Select, Space, Switch, Tag, Tooltip, Typography,
} from 'antd';
import { fmtMomentoBr } from '../../utils/dataFmt';
import { fu, erroDe, type FollowupConfig, type Placeholder, type Previa, type ResumoRodada, type Modo } from './api';
import { DIAS_SEMANA, ROTULO_MODO, fraseDaCadencia, fraseDaJanela, fraseDaRodada, inserirCampo, janelaValida } from './regras';

const { Text, Title, Paragraph } = Typography;

type Edicao = Partial<FollowupConfig>;
const CAMPOS_SALVAVEIS: (keyof FollowupConfig)[] = [
  'primeiro_apos_dias', 'intervalo_dias', 'max_tentativas', 'modo_padrao', 'envio_automatico_ativo', 'janela_dias_semana',
  'janela_hora_inicio', 'janela_hora_fim', 'template_assunto', 'template_corpo', 'copia_remetente', 'perda_automatica',
  'perder_apos_dias', 'aviso_perda_dias',
];

export default function AbaRegras() {
  const { message, modal } = AntApp.useApp();
  const [salvo, setSalvo] = useState<FollowupConfig | null>(null);
  const [ed, setEd] = useState<Edicao>({});
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [podeConfigurar, setPodeConfigurar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [negocios, setNegocios] = useState<{ id: number; titulo: string; cliente: string | null }[]>([]);
  const [negocioPrevia, setNegocioPrevia] = useState<number | undefined>();
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [ensaio, setEnsaio] = useState<ResumoRodada | null>(null);
  const [ensaiando, setEnsaiando] = useState(false);
  const corpoRef = useRef<any>(null);
  const assuntoRef = useRef<any>(null);
  const [ultimoFoco, setUltimoFoco] = useState<'assunto' | 'corpo'>('corpo');

  const carregar = useCallback(async () => {
    try {
      const r = await fu.config();
      setSalvo(r.config); setEd({}); setPlaceholders(r.placeholders); setPodeConfigurar(r.pode_configurar);
      const f = await fu.fila();
      setNegocios(f.itens.map(i => ({ id: i.id, titulo: i.titulo, cliente: i.cliente })));
      const comEmail = f.itens.find(i => i.contato_email);
      setNegocioPrevia(prev => prev ?? comEmail?.id ?? f.itens[0]?.id);
    } catch (e) { message.error(`Não consegui carregar a regra: ${erroDe(e)}`); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  const c = useMemo(() => (salvo ? { ...salvo, ...ed } as FollowupConfig : null), [salvo, ed]);
  const sujo = !!salvo && CAMPOS_SALVAVEIS.some(k => ed[k] !== undefined && JSON.stringify(ed[k]) !== JSON.stringify(salvo[k]));
  const mudar = (k: keyof FollowupConfig, v: any) => setEd(prev => ({ ...prev, [k]: v }));

  // A prévia acompanha o texto em edição (com uma pausa): o "Enviar teste" só
  // libera quando o que está na tela é exatamente o que vai para a caixa.
  const chaveAtual = c ? JSON.stringify([negocioPrevia ?? null, c.template_assunto, c.template_corpo]) : '';
  const [previaDe, setPreviaDe] = useState('');
  const verPrevia = useCallback(async () => {
    if (!c) return;
    const chave = JSON.stringify([negocioPrevia ?? null, c.template_assunto, c.template_corpo]);
    setCarregandoPrevia(true);
    try { setPrevia(await fu.previa({ proposta_id: negocioPrevia, assunto: c.template_assunto, corpo: c.template_corpo })); setPreviaDe(chave); }
    catch (e) { message.error(erroDe(e)); }
    finally { setCarregandoPrevia(false); }
  }, [c, negocioPrevia, message]);
  useEffect(() => {
    if (!salvo || !negocioPrevia) return;
    const t = setTimeout(() => { verPrevia(); }, 700);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salvo, chaveAtual]);

  if (!c) return <Card loading />;
  const so = !podeConfigurar;

  const ligarComConfirmacao = (campo: 'envio_automatico_ativo' | 'perda_automatica', valor: boolean) => {
    if (!valor) { mudar(campo, false); return; }
    modal.confirm({
      title: campo === 'envio_automatico_ativo' ? 'Ligar o e-mail automático?' : 'Ligar a perda automática?',
      content: campo === 'envio_automatico_ativo'
        ? 'Depois de salvar, os negócios em “E-mail automático” passam a receber o e-mail do responsável na janela da regra, sem ninguém clicar. Os outros continuam como estão.'
        : `Depois de salvar, negócio sem resposta ${c.perder_apos_dias} dias depois do último follow-up vira PERDIDO sozinho, com o motivo “Sem retorno do cliente”. O responsável é avisado ${c.aviso_perda_dias} dia(s) antes.`,
      okText: 'Ligar', cancelText: 'Voltar',
      onOk: () => mudar(campo, true),
    });
  };

  const janelaOk = janelaValida(c.janela_hora_inicio, c.janela_hora_fim);
  const salvar = async () => {
    const mudancas: Edicao = {};
    for (const k of CAMPOS_SALVAVEIS) if (ed[k] !== undefined && JSON.stringify(ed[k]) !== JSON.stringify(salvo![k])) (mudancas as any)[k] = ed[k];
    // A janela vai sempre inteira: o servidor confere início × fim juntos.
    if (mudancas.janela_hora_inicio !== undefined || mudancas.janela_hora_fim !== undefined) {
      mudancas.janela_hora_inicio = c.janela_hora_inicio;
      mudancas.janela_hora_fim = c.janela_hora_fim;
    }
    setSalvando(true);
    try { const r = await fu.salvarConfig(mudancas); setSalvo(r.config); setEd({}); message.success('Regra salva'); }
    catch (e) { message.error(erroDe(e)); }
    finally { setSalvando(false); }
  };

  const inserir = (chave: string) => {
    const alvo = ultimoFoco === 'assunto' ? 'template_assunto' : 'template_corpo';
    const ref = ultimoFoco === 'assunto' ? assuntoRef.current?.input : corpoRef.current?.resizableTextArea?.textArea;
    const r = inserirCampo(c[alvo], chave, ref?.selectionStart, ref?.selectionEnd);
    mudar(alvo, r.texto);
    setTimeout(() => { try { ref?.focus(); ref?.setSelectionRange(r.cursor, r.cursor); } catch { /* sem foco */ } }, 0);
  };

  const enviarTeste = async () => {
    try {
      const r = await fu.teste({ proposta_id: negocioPrevia, assunto: c.template_assunto, corpo: c.template_corpo });
      message.success(`Teste enviado para ${r.enviado_para}. Confira a caixa de entrada.`
        + (r.bloqueado_por.length ? ' Atenção: para o cliente este e-mail NÃO sairia — viraria tarefa.' : ''));
    } catch (e) { message.error(erroDe(e)); }
  };

  const rodarEnsaio = async () => {
    setEnsaiando(true);
    try { setEnsaio(await fu.rodar(true)); } catch (e) { message.error(erroDe(e)); } finally { setEnsaiando(false); }
  };

  const numero = (k: keyof FollowupConfig, min: number, max: number, sufixo: string) => (
    <InputNumber size="small" min={min} max={max} value={c[k] as number} disabled={so} style={{ width: 90 }}
      onChange={v => v != null && mudar(k, Number(v))} suffix={sufixo} />
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', gap: 16, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        {so && <Alert type="info" showIcon message="Você vê a regra, mas só quem tem “Configurar follow-ups” muda." />}

        <Card size="small" title="Cadência">
          <Space orientation="vertical" size={10} style={{ width: '100%' }}>
            <Space wrap>1º follow-up {numero('primeiro_apos_dias', 1, 180, 'dias')} depois de entrar em Follow-up</Space>
            <Space wrap>depois, a cada {numero('intervalo_dias', 1, 180, 'dias')} até {numero('max_tentativas', 1, 12, 'vezes')}</Space>
            <Space wrap>
              Negócio que entra em Follow-up começa como
              <Select size="small" style={{ width: 180 }} value={c.modo_padrao} disabled={so} onChange={(v: Modo) => mudar('modo_padrao', v)}
                options={(['manual', 'automatico', 'desligado'] as Modo[]).map(m => ({ value: m, label: ROTULO_MODO[m] }))} />
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              “Tarefa” cria a atividade de follow-up para o responsável no dia (aparece no Meu Radar); ela conta quando é concluída.
              “E-mail automático” manda o e-mail sozinho — se faltar algum dado, vira tarefa com o motivo.
            </Text>
          </Space>
        </Card>

        <Card size="small" title={<Space align="center" wrap>E-mail automático
          <Switch checked={c.envio_automatico_ativo} disabled={so} onChange={v => ligarComConfirmacao('envio_automatico_ativo', v)} />
          <Text type={c.envio_automatico_ativo ? 'success' : 'secondary'}>{c.envio_automatico_ativo ? 'ligado' : 'desligado'}</Text></Space>}>
          <Space orientation="vertical" size={10} style={{ width: '100%' }}>
            <Space wrap>
              Sai em
              <Checkbox.Group value={c.janela_dias_semana} disabled={so} onChange={v => mudar('janela_dias_semana', (v as number[]).slice().sort())}
                options={DIAS_SEMANA.map(d => ({ value: d.valor, label: d.curto }))} />
            </Space>
            <Space wrap>das {numero('janela_hora_inicio', 0, 23, 'h')} às {numero('janela_hora_fim', 1, 24, 'h')} (Brasília) — {fraseDaJanela(c)}</Space>
            {!janelaOk && <Text type="danger">A janela tem de terminar depois de começar.</Text>}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Switch size="small" checked={c.copia_remetente} disabled={so} onChange={v => mudar('copia_remetente', v)} style={{ flex: 'none', marginTop: 3 }} />
              <span>Cópia oculta para quem envia (o e-mail aparece na caixa dele)</span>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sai pelo sistema com o nome e o endereço do responsável do negócio, e a resposta do cliente volta para ele.
              Passar a sair pelo Outlook de cada um depende de uma autorização no Microsoft 365.
            </Text>
          </Space>
        </Card>

        <Card size="small" title="Texto do e-mail">
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            <Input ref={assuntoRef} addonBefore="Assunto" value={c.template_assunto} disabled={so} maxLength={300}
              onFocus={() => setUltimoFoco('assunto')} onChange={e => mudar('template_assunto', e.target.value)} />
            <Input.TextArea ref={corpoRef} value={c.template_corpo} disabled={so} maxLength={5000} showCount
              autoSize={{ minRows: 9, maxRows: 22 }} onFocus={() => setUltimoFoco('corpo')} onChange={e => mudar('template_corpo', e.target.value)} />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Clique para inserir no {ultimoFoco === 'assunto' ? 'assunto' : 'texto'}:</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {placeholders.map(p => (
                  <Tooltip key={p.chave} title={p.descricao}>
                    <Tag style={{ cursor: so ? 'default' : 'pointer', marginInlineEnd: 0 }} onClick={() => !so && inserir(p.chave)}>{`{{${p.chave}}}`}</Tag>
                  </Tooltip>
                ))}
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Só a linha de cargo ou telefone vazios some da assinatura. Qualquer outro campo vazio — ou escrito errado — segura o
              e-mail: “Olá, !” não sai para o cliente; vira tarefa com o motivo.
            </Text>
          </Space>
        </Card>

        <Card size="small" title={<Space align="center" wrap>Sem resposta
          <Switch checked={c.perda_automatica} disabled={so} onChange={v => ligarComConfirmacao('perda_automatica', v)} />
          <Text type={c.perda_automatica ? 'warning' : 'secondary'}>{c.perda_automatica ? 'perde sozinho' : 'só marca na fila'}</Text></Space>}>
          <Space orientation="vertical" size={10} style={{ width: '100%' }}>
            <Space wrap>Depois do último follow-up, sem resposta por {numero('perder_apos_dias', 1, 365, 'dias')}
              → perdido, motivo “Sem retorno do cliente”</Space>
            <Space wrap>Avisar o responsável {numero('aviso_perda_dias', 0, 60, 'dias')} antes</Space>
          </Space>
        </Card>

        <Alert type="info" showIcon message={fraseDaCadencia(c)} />
        {!so && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button type="primary" onClick={salvar} disabled={!sujo || !janelaOk} loading={salvando}>Salvar a regra</Button>
            <Button onClick={() => setEd({})} disabled={!sujo}>Desfazer</Button>
            {salvo?.atualizado_em && <Text type="secondary" style={{ fontSize: 12 }}>Salva em {fmtMomentoBr(salvo.atualizado_em)} por {salvo.atualizado_por}</Text>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, position: 'sticky', top: 8 }}>
        <Card size="small" title="Prévia com um negócio de verdade">
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            <Space wrap style={{ width: '100%' }}>
              <Select showSearch optionFilterProp="label" style={{ width: 340, maxWidth: '100%' }} placeholder="Escolha um negócio em Follow-up"
                value={negocioPrevia} onChange={setNegocioPrevia}
                options={negocios.map(n => ({ value: n.id, label: `${n.titulo}${n.cliente ? ` — ${n.cliente}` : ''}` }))} />
              <Button onClick={verPrevia} loading={carregandoPrevia}>Atualizar prévia</Button>
            </Space>
            {previa && !previa.vazio && (
              <>
                {previa.bloqueado_por.length > 0
                  ? <Alert type="warning" showIcon message="Este e-mail não sairia — viraria tarefa:" description={previa.bloqueado_por.join('; ')} />
                  : <Alert type="success" showIcon message="Este e-mail sairia assim." />}
                <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px' }}>
                  <Text type="secondary">De</Text><Text>{previa.remetente?.exibido ? `${previa.remetente.nome} <${previa.remetente.email}>` : '—'}</Text>
                  <Text type="secondary">Para</Text><Text>{previa.destinatario || '—'}</Text>
                  <Text type="secondary">Assunto</Text><Text strong>{previa.assunto}</Text>
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'Arial, Helvetica, sans-serif' }}>{previa.texto}</Paragraph>
                {!so && (
                  <Tooltip title={previaDe !== chaveAtual ? 'Atualizando a prévia com o texto editado…' : 'Vai para o seu e-mail, como está acima'}>
                    <Button onClick={enviarTeste} disabled={previaDe !== chaveAtual || carregandoPrevia}>Enviar teste para mim</Button>
                  </Tooltip>
                )}
              </>
            )}
            {previa?.vazio && <Text type="secondary">Nenhum negócio em Follow-up com contato para montar a prévia.</Text>}
          </Space>
        </Card>
        <Card size="small" title="Ensaio da próxima rodada">
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Mostra o que a rodada faria agora com a regra SALVA — sem enviar nem criar nada. A rodada roda sozinha de hora em hora;
              o e-mail só sai dentro da janela.
            </Text>
            {!so && <Button onClick={rodarEnsaio} loading={ensaiando}>Ver o que faria agora</Button>}
            {salvo?.ultima_rodada_em && <Text type="secondary" style={{ fontSize: 12 }}>
              Última rodada de verdade: {fmtMomentoBr(salvo.ultima_rodada_em)} — {fraseDaRodada(salvo.ultima_rodada)}</Text>}
          </Space>
        </Card>
      </div>

      {ensaio && (
        <Modal open title="Ensaio: o que a rodada faria agora" onCancel={() => setEnsaio(null)} footer={null} destroyOnHidden
          width="94vw" style={{ maxWidth: 820, top: 16 }} styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}>
          <Title level={5} style={{ marginTop: 0 }}>Faria agora: {fraseDaRodada(ensaio, true)}</Title>
          {!ensaio.janela_aberta && <Alert type="info" showIcon style={{ marginBottom: 8 }} message="Agora está fora da janela de envio — os e-mails esperariam a janela." />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(ensaio.itens || []).filter(i => !['agendado', 'aguardando', 'tarefa_em_aberto'].includes(i.acao)).map(i => (
              <div key={i.id}><Tag>{rotuloAcao(i.acao)}</Tag>{i.titulo}{i.motivo ? <Text type="secondary"> — {i.motivo}</Text> : null}</div>
            ))}
            {!(ensaio.itens || []).some(i => !['agendado', 'aguardando', 'tarefa_em_aberto'].includes(i.acao)) && <Text type="secondary">Nada a fazer agora.</Text>}
          </div>
        </Modal>
      )}
    </div>
  );
}

function rotuloAcao(a: string): string {
  return ({
    email: '✉️ e-mail', tarefa: '📋 tarefa', perder: '✗ perder', avisar_perda: '⚠ aviso de perda', fora_da_janela: '⏳ espera a janela',
    tarefa_concluida: '✓ conta a tarefa', tarefa_sumiu: 'tarefa apagada', tarefa_cancelada: 'tarefa cancelada — reagenda',
    reconciliado: 'acerta a contagem', em_andamento: 'em andamento', tarefa_reapontada: 'tarefa já existe', falhou: 'falhou', erro: 'erro',
  } as Record<string, string>)[a] || a;
}
